/**
 * 人材（タレント）管理用の API hooks
 */

import { RestApiError, restClient } from "lib/rest-client";
import { useCallback, useEffect, useState } from "react";

/**
 * タレントスキル項目の型
 */
export type TalentSkillItem = {
  id: string;
  skillName: string;
  skillMasterId: string | null;
  experienceYears: number | null;
};

/**
 * タレント一覧アイテムの型
 */
export type TalentListItem = {
  id: string;
  name: string;
  affiliation: string | null;
  employmentType: string | null;
  email: string | null;
  nearestStation: string | null;
  desiredReward: number | null;
  isActive: boolean;
  assignedTo: string | null;
  assigneeName: string | null;
  skills: string[];
  workStatus: string | null;
  activePipelineCount: number;
  currentContractEndDate: string | null;
  createdAt: string;
};

/**
 * タレント詳細の型
 */
export type TalentDetail = {
  id: string;
  name: string;
  nameKana: string | null;
  affiliation: string | null;
  partnerCompany: string | null;
  partnerContact: string | null;
  employmentType: string | null;
  email: string | null;
  phone: string | null;
  nearestStation: string | null;
  desiredReward: number | null;
  remotePreference: string | null;
  age: number | null;
  availableFrom: string | null;
  isActive: boolean;
  assignedTo: string | null;
  assigneeName: string | null;
  slackMemberId: string | null;
  notes: string | null;
  sourceType: string | null;
  sourceEmailId: string | null;
  sourceSnapshot: string | null;
  skills: TalentSkillItem[];
  workStatus: string | null;
  activePipelineCount: number;
  currentContractEndDate: string | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * タレント一覧レスポンスの型
 */
type TalentListResponse = {
  items: TalentListItem[];
  total: number;
  page: number;
  limit: number;
};

/**
 * タレント一覧フィルターの型
 */
export type TalentFilters = {
  search?: string;
  affiliation?: string;
  employmentType?: string;
  isActive?: boolean;
  assignedTo?: string;
  page?: number;
  limit?: number;
};

/**
 * フィルターをクエリパラメータに変換
 * @param {object} filters フィルター
 * @return {Record<string, string>} クエリパラメータ
 */
function filtersToParams(filters: TalentFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.search) params.search = filters.search;
  if (filters.affiliation) params.affiliation = filters.affiliation;
  if (filters.employmentType) params.employmentType = filters.employmentType;
  if (filters.isActive !== undefined)
    params.isActive = String(filters.isActive);
  if (filters.assignedTo) params.assignedTo = filters.assignedTo;
  if (filters.page !== undefined) params.page = String(filters.page);
  if (filters.limit !== undefined) params.limit = String(filters.limit);
  return params;
}

/**
 * タレント一覧を取得する hook
 * @param {object} filters フィルター条件
 * @param {string} [filters.search] 検索キーワード
 * @param {string} [filters.affiliation] 所属
 * @param {string} [filters.employmentType] 雇用形態
 * @param {boolean} [filters.isActive] アクティブ状態
 * @param {string} [filters.assignedTo] 担当者
 * @param {number} [filters.page] ページ番号
 * @param {number} [filters.limit] 取得件数
 * @return {object} talents, total, loading, error, refetch
 */
export function useTalents(filters: TalentFilters = {}): {
  talents: TalentListItem[];
  total: number;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [talents, setTalents] = useState<TalentListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const filterKey = JSON.stringify(filters);

  const fetchTalents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = filtersToParams(JSON.parse(filterKey) as TalentFilters);
      const response = await restClient.get<TalentListResponse>(
        "/talents",
        params,
      );
      setTalents(response.items);
      setTotal(response.total);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [filterKey]);

  useEffect(() => {
    fetchTalents();
  }, [fetchTalents]);

  return { talents, total, loading, error, refetch: fetchTalents };
}

/**
 * タレント詳細を取得する hook
 * @param {string | undefined} id タレントID
 * @return {object} talent, loading, error, refetch
 */
export function useTalentDetail(id: string | undefined): {
  talent: TalentDetail | null;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [talent, setTalent] = useState<TalentDetail | null>(null);
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchTalent = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await restClient.get<TalentDetail>(`/talents/${id}`);
      setTalent(response);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTalent();
  }, [fetchTalent]);

  return { talent, loading, error, refetch: fetchTalent };
}

/**
 * タレントを作成する hook
 * @return {object} createTalent, loading, error
 */
export function useCreateTalent(): {
  createTalent: (data: Partial<TalentDetail>) => Promise<TalentDetail>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const createTalent = useCallback(
    async (data: Partial<TalentDetail>): Promise<TalentDetail> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.post<TalentDetail>("/talents", data);
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createTalent, loading, error };
}

/**
 * タレントを更新する hook
 * @return {object} updateTalent, loading, error
 */
export function useUpdateTalent(): {
  updateTalent: (
    id: string,
    data: Partial<TalentDetail>,
  ) => Promise<TalentDetail>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const updateTalent = useCallback(
    async (id: string, data: Partial<TalentDetail>): Promise<TalentDetail> => {
      setLoading(true);
      setError(null);

      try {
        const response = await restClient.put<TalentDetail>(
          `/talents/${id}`,
          data,
        );
        return response;
      } catch (e) {
        const apiError =
          e instanceof RestApiError
            ? e
            : new RestApiError(
                e instanceof Error ? e.message : "Unknown error",
                500,
              );
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateTalent, loading, error };
}

/**
 * タレントを削除する hook
 * @return {object} deleteTalent, loading, error
 */
export function useDeleteTalent(): {
  deleteTalent: (id: string) => Promise<void>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const deleteTalent = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await restClient.delete(`/talents/${id}`);
    } catch (e) {
      const apiError =
        e instanceof RestApiError
          ? e
          : new RestApiError(
              e instanceof Error ? e.message : "Unknown error",
              500,
            );
      setError(apiError);
      throw apiError;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteTalent, loading, error };
}
